"use client";

import { c as _c } from "react/compiler-runtime";
import { clsx as cn } from "@/helpers/clsx";
import { OauthDialog } from "@openapi/playground/auth/oauth-dialog";
import type { FetchResult } from "@openapi/playground/fetcher";
import type { PrimitiveRequestField, ReferenceSchema, RequestSchema } from "@openapi/playground/index";
import type { RequestData } from "@openapi/requests/_shared";
import { MethodLabel } from "@openapi/ui/components/method-label";
import { useApiContext, useServerSelectContext } from "@openapi/ui/contexts/api";
import { useRequestData } from "@openapi/ui/contexts/code-example";
import ServerSelect from "@openapi/ui/server-select";
import type { Security } from "@openapi/utils/get-security";
import { getUrl } from "@openapi/utils/server-url";
import { useQuery } from "@openapi/utils/use-query";
import { DynamicCodeBlock } from "fumadocs-ui/components/dynamic-codeblock";
import { buttonVariants } from "fumadocs-ui/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "fumadocs-ui/components/ui/collapsible";
import { ChevronDown, Loader2 } from "lucide-react";
import { type FC, Fragment, type HTMLAttributes, type ReactElement, type ReactNode, type RefObject, createContext, useContext, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import type { ControllerFieldState, ControllerRenderProps, FieldPath, UseFormStateReturn } from "react-hook-form";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { FieldSet, ObjectInput } from "./inputs";
import { getStatusInfo } from "./status-info";
interface FormValues {
  authorization: string | {
    username: string;
    password: string;
  };
  path: Record<string, string>;
  query: Record<string, string>;
  header: Record<string, string>;
  cookie: Record<string, string>;
  body: unknown;
}
export interface CustomField<TName extends FieldPath<FormValues>, Info> {
  render: (props: {
    /**
     * Field Info
     */
    info: Info;
    field: ControllerRenderProps<FormValues, TName>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<FormValues>;
  }) => ReactElement;
}
export type ClientProps = HTMLAttributes<HTMLFormElement> & {
  route: string;
  method: string;
  authorization?: Security & {
    persistentId: string;
  };
  parameters?: PrimitiveRequestField[];
  body?: RequestSchema & {
    mediaType: string;
  };
  /**
   * Resolver for reference schemas you've passed
   */
  references: Record<string, RequestSchema>;
  proxyUrl?: string;
  fields?: {
    parameter?: CustomField<`${PrimitiveRequestField["in"]}.${string}`, PrimitiveRequestField>;
    auth?: CustomField<"authorization", RequestSchema>;
    body?: CustomField<"body", RequestSchema>;
  };
  components?: Partial<{
    ResultDisplay: FC<{
      data: FetchResult;
    }>;
  }>;
};
interface SchemaContextType {
  references: Record<string, RequestSchema>;
  dynamic: RefObject<Map<string, DynamicField>>;
}
export type DynamicField = {
  type: "object";
  properties: string[];
} | {
  type: "field";
  schema: RequestSchema | ReferenceSchema;
};
const SchemaContext = createContext<SchemaContextType | undefined>(undefined);
export function useSchemaContext() {
  const ctx = useContext(SchemaContext);
  if (!ctx) {
    throw new Error("Missing provider");
  }
  return ctx;
}
function toRequestData(method: string, mediaType: string | undefined, value: FormValues): RequestData {
  return {
    path: value.path,
    method,
    header: value.header,
    body: value.body && typeof value.body === "object" && !Object.keys(value.body).length ? undefined : value.body,
    bodyMediaType: mediaType as RequestData["bodyMediaType"] ?? "application/json",
    cookie: value.cookie,
    query: value.query
  };
}
export default function Client({
  route,
  method = "GET",
  authorization,
  parameters,
  body,
  fields,
  references,
  proxyUrl,
  components: {
    ResultDisplay = DefaultResultDisplay
  } = {},
  ...rest
}: ClientProps) {
  const {
    server
  } = useServerSelectContext();
  const dynamicRef = useRef(new Map<string, DynamicField>());
  const requestData = useRequestData();
  const authInfo = usePersistentAuthInfo(authorization);
  const defaultValues: FormValues = useMemo(() => ({
    authorization: authInfo.info,
    path: {},
    query: Object.keys(requestData.data.query || {}).reduce((acc, key) => {
      acc[key] = "";
      return acc;
    }, {} as Record<string, string>),
    header: requestData.data.header,
    body: {},
    cookie: requestData.data.cookie
  }), [authInfo.info, requestData.data]);
  const form = useForm<FormValues>({
    defaultValues
  });
  const testQuery = useQuery(async (input: FormValues) => {
    const fetcher = await import("./fetcher").then(mod => mod.createBrowserFetcher());
    const serverUrl = server ? getUrl(server.url, server.variables) : window.location.origin;
    const requestData = toRequestData(method, body?.mediaType, input);
    return fetcher.fetch(`${serverUrl}${route}`, {
      proxyUrl,
      ...requestData,
      header: {
        ...requestData.header,
        Authorization: String(authInfo.info || "")
      }
    });
  });
  useEffect(() => {
    if (!testQuery.isLoading && (testQuery.data || testQuery.error)) {
      const result = document.getElementById("result");
      if (result) {
        result.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    }
  }, [testQuery.isLoading]);
  useEffect(() => {
    form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- update default value
  }, [defaultValues]);
  useEffect(() => {
    const subscription = form.watch(_value => {
      const value = _value as FormValues;
      queueMicrotask(() => {
        requestData.saveData(toRequestData(method, body?.mediaType, value));
      });
    });
    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mounted only once
  }, []);
  const onSubmit = form.handleSubmit(value => {
    testQuery.start(value);
  });
  return <FormProvider {...form}>
      <SchemaContext.Provider value={useMemo(() => ({
      references: references,
      dynamic: dynamicRef
    }), [references])}>
        <AuthProvider authorization={authorization}>
          <form {...rest} className={cn("not-prose flex flex-col gap-3 rounded-2xl border p-3 pt-0 shadow-md", rest.className)} onSubmit={onSubmit}>
            <div className="sticky top-[60px] -mb-3 flex flex-row items-center gap-2 bg-[#0E0E0E80] pb-3 pt-3 text-sm backdrop-blur-sm">
              <MethodLabel>{method}</MethodLabel>
              <Route route={route} className="flex-1" />
              <button type="submit" className={cn(buttonVariants({
              color: "secondary",
              size: "sm",
              className: "!bg-fd-primary text-fd-primary-foreground"
            }), "px-3 py-1.5")} disabled={testQuery.isLoading}>
                {testQuery.isLoading ? <Loader2 className="animate-spin size-4" /> : "Send"}
              </button>
            </div>
            <FormBody body={body} fields={fields} parameters={parameters} authorization={authorization} />
            {testQuery.data ? <ResultDisplay data={testQuery.data} /> : null}
          </form>
        </AuthProvider>
      </SchemaContext.Provider>
    </FormProvider>;
}
const paramNames = ["Headers", "Cookies", "Path", "Query Params"] as const;
const paramTypes = ["header", "cookie", "path", "query"] as const;
function FormBody(t0) {
  const $ = _c(25);
  const {
    authorization,
    parameters: t1,
    body,
    fields: t2
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const parameters = t3;
  let t4;
  if ($[2] !== t2) {
    t4 = t2 === undefined ? {} : t2;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const fields = t4;
  const {
    servers
  } = useApiContext();
  const {
    server,
    setServer,
    setServerVariables
  } = useServerSelectContext();
  let t5;
  if ($[4] !== parameters) {
    t5 = paramTypes.map(param => parameters.filter(v => v.in === param));
    $[4] = parameters;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const params = t5;
  let t6;
  if ($[6] !== server || $[7] !== servers.length || $[8] !== setServer || $[9] !== setServerVariables) {
    t6 = servers.length > 1 ? <CollapsiblePanel title="Server URL"><ServerSelect server={server} onServerChanged={setServer} onVariablesChanged={setServerVariables} /></CollapsiblePanel> : null;
    $[6] = server;
    $[7] = servers.length;
    $[8] = setServer;
    $[9] = setServerVariables;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== authorization || $[12] !== fields || $[13] !== params) {
    let t8;
    if ($[15] !== authorization || $[16] !== fields) {
      t8 = (param_0, i) => {
        const name = paramNames[i];
        const type = paramTypes[i];
        if (type !== "header" && param_0.length === 0) {
          return;
        }
        if (type === "header" && !authorization && param_0.length === 0) {
          return;
        }
        if (type === "header") {
          return null;
        }
        return <CollapsiblePanel key={name} title={name} defaultOpen={type === "query"}>{param_0.map(field => {
            const fieldName = `${type}.${field.name}` as const;
            if (!fields?.parameter) {
              return <FieldSet key={fieldName} name={field.name} fieldName={fieldName} field={field} />;
            }
            return renderCustomField(fieldName, field, fields.parameter, field.name);
          })}</CollapsiblePanel>;
      };
      $[15] = authorization;
      $[16] = fields;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t7 = params.map(t8);
    $[11] = authorization;
    $[12] = fields;
    $[13] = params;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[18] !== body || $[19] !== fields) {
    t8 = body ? <CollapsiblePanel title="Body">{fields.body ? renderCustomField("body", body, fields.body) : body.type === "object" ? <ObjectInput field={body} fieldName="body" /> : <FieldSet field={body} fieldName="body" />}</CollapsiblePanel> : null;
    $[18] = body;
    $[19] = fields;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <>{t6}{t7}{t8}</>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
function renderCustomField(fieldName: string, info: RequestSchema & {
  name?: string;
}, field: CustomField<never, never>, key?: string) {
  return <Controller key={key}
  // @ts-expect-error we use string here
  render={props => field.render({
    ...props,
    info
  })} name={fieldName} />;
}
function AuthProvider(t0) {
  const $ = _c(9);
  const {
    authorization,
    children
  } = t0;
  const form = useFormContext();
  if (!authorization || authorization.type !== "oauth2") {
    return children;
  }
  let t1;
  if ($[0] !== authorization.flows) {
    t1 = Object.keys(authorization.flows);
    $[0] = authorization.flows;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const flow = t1[0];
  const t2 = flow as keyof typeof authorization.flows;
  let t3;
  if ($[2] !== form) {
    t3 = token => form.setValue("authorization", `Bearer ${token}`);
    $[2] = form;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== authorization || $[5] !== children || $[6] !== t2 || $[7] !== t3) {
    t4 = <OauthDialog flow={t2} scheme={authorization} setToken={t3}>{children}</OauthDialog>;
    $[4] = authorization;
    $[5] = children;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function Route(t0) {
  const $ = _c(8);
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      route,
      ...props
    } = t0;
    const segments = route.split("/").filter(_temp);
    t1 = props;
    t2 = cn("flex flex-row items-center gap-0.5 overflow-auto text-nowrap", props.className);
    t3 = segments.map(_temp2);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = <div {...t1} className={t2}>{t3}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function _temp2(part_0, index) {
  return <Fragment key={index}><span className="text-fd-muted-foreground">/</span>{part_0.startsWith("{") && part_0.endsWith("}") ? <code className="bg-fd-primary/10 text-fd-primary">{part_0}</code> : <code className="text-fd-foreground">{part_0}</code>}</Fragment>;
}
function _temp(part) {
  return part.length > 0;
}
function DefaultResultDisplay(t0) {
  const $ = _c(20);
  const {
    data
  } = t0;
  let t1;
  if ($[0] !== data.status) {
    t1 = getStatusInfo(data.status);
    $[0] = data.status;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const statusInfo = t1;
  const {
    shikiOptions
  } = useApiContext();
  const T0 = statusInfo.icon;
  let t2;
  if ($[2] !== statusInfo.color) {
    t2 = cn("size-4", statusInfo.color);
    $[2] = statusInfo.color;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== statusInfo.icon || $[5] !== t2) {
    t3 = <T0 className={t2} />;
    $[4] = statusInfo.icon;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== statusInfo.description || $[8] !== t3) {
    t4 = <div className="inline-flex items-center gap-1.5 text-sm font-medium text-fd-foreground">{t3}{statusInfo.description}</div>;
    $[7] = statusInfo.description;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== data.status) {
    t5 = <p className="text-sm text-fd-muted-foreground">{data.status}</p>;
    $[10] = data.status;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== data.data || $[13] !== data.type || $[14] !== shikiOptions) {
    t6 = data.data ? <DynamicCodeBlock lang={typeof data.data === "string" && data.data.length > 50000 ? "text" : data.type} code={typeof data.data === "string" ? data.data : JSON.stringify(data.data, null, 2)} options={shikiOptions} /> : null;
    $[12] = data.data;
    $[13] = data.type;
    $[14] = shikiOptions;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t4 || $[17] !== t5 || $[18] !== t6) {
    t7 = <div id="result" className="flex scroll-mt-[50px] flex-col gap-3 rounded-lg border bg-fd-card p-4">{t4}{t5}{t6}</div>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function usePersistentAuthInfo(authorization) {
  const $ = _c(10);
  const key = authorization ? `__fumadocs_auth_${authorization.persistentId}` : null;
  let t0;
  if ($[0] !== authorization) {
    t0 = () => {
      if (!authorization || authorization.type === "apiKey") {
        return "";
      }
      if (authorization.type === "http" && authorization.scheme === "basic") {
        return {
          username: "",
          password: ""
        };
      }
      return "Bearer";
    };
    $[0] = authorization;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [info, setInfo] = useState(t0);
  let t1;
  let t2;
  if ($[2] !== key) {
    t1 = () => {
      if (!key) {
        return;
      }
      const item = localStorage.getItem(key);
      if (item) {
        setInfo(JSON.parse(item));
      }
    };
    t2 = [key];
    $[2] = key;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== key) {
    t3 = value => {
      if (!key) {
        return;
      }
      localStorage.setItem(key, JSON.stringify(value));
      setInfo(value);
    };
    $[5] = key;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = useEffectEvent(t3);
  let t5;
  if ($[7] !== info || $[8] !== t4) {
    t5 = {
      info,
      saveInfo: t4
    };
    $[7] = info;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function CollapsiblePanel(t0) {
  const $ = _c(7);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <div className="group inline-flex w-full items-center gap-2 rounded-t-[inherit] bg-neutral-950/50 p-3 py-4 text-sm font-medium leading-none">{title}</div>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <div className="flex flex-col gap-3 divide-y border-t p-3 pt-0 *:pt-3">{children}</div>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="rounded-xl border bg-fd-card text-fd-card-foreground">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function writeAuthHeader(authorization: Security, input: FormValues["authorization"], header: Record<string, unknown>, query: Record<string, unknown>, cookie: Record<string, string>) {
  if (authorization.type === "apiKey") {
    if (authorization.in === "header") {
      header[authorization.name] = input as string;
    }
    if (authorization.in === "query") {
      query[authorization.name] = input as string;
    }
    if (authorization.in === "cookie") {
      cookie[authorization.name] = input as string;
    }
    return;
  }
  if (authorization.type === "http" && authorization.scheme === "basic" && typeof input === "object") {
    header.Authorization = `Basic ${btoa(`${input.username}:${input.password}`)}`;
    return;
  }
  if (typeof input === "string") {
    header.Authorization = input;
  }
}
export const Auth = () => {
  const $ = _c(16);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      persistentId: "BearerAuth",
      type: "http",
      scheme: "bearer",
      bearerFormat: "API Key",
      description: "API key authentication. Get your API key from your [Fundra workspace settings](https://fundra.com/workspace/settings/api).",
      scopes: []
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const authorization = t0;
  const authInfo = usePersistentAuthInfo(authorization);
  const requestData = useRequestData();
  let t1;
  if ($[1] !== authInfo.info) {
    t1 = typeof authInfo.info === "string" ? authInfo.info.replace(/^Bearer\s*/i, "") : "";
    $[1] = authInfo.info;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const token = t1;
  let t2;
  if ($[3] !== requestData || $[4] !== token) {
    t2 = () => {
      requestData.saveAuthorization(`Bearer ${token || "<token>"}`);
    };
    $[3] = requestData;
    $[4] = token;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== token) {
    t3 = [token];
    $[6] = token;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-fd-muted-foreground">Credentials</p>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="shrink-0 text-sm text-fd-muted-foreground">Bearer</span>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== authInfo || $[11] !== requestData) {
    t6 = e => {
      const value = e.target.value ? `Bearer ${e.target.value}` : "Bearer <token>";
      authInfo.saveInfo(e.target.value ? value : "");
      requestData.saveAuthorization(value);
    };
    $[10] = authInfo;
    $[11] = requestData;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6 || $[14] !== token) {
    t7 = <div className="rounded-xl border bg-fd-card p-3 pt-0 text-fd-card-foreground">{t4}<div className="flex items-center gap-2 rounded-lg border bg-fd-background px-3 py-2">{t5}<input type="text" placeholder="token" value={token} onChange={t6} className="w-full bg-transparent text-sm text-fd-foreground placeholder:text-fd-muted-foreground/50 focus:outline-none" /></div></div>;
    $[13] = t6;
    $[14] = token;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};